<template>
  <div>
    <fin-data-loader :resolver="loadData">
      <template #content>
        <div
          v-if="!isCompareGroupMode && $route.name !== 'addShareRateToPortfolio' && $route.name !== 'addShareRateExecToPortfolio'">
          <div v-if="rating" style="background-color: #fff;">
            <v-container>
              <v-row align="center">
                <v-col cols="12" md="8" sm="7">
                  <fin-lexicon-text auto-solve class="method-info mb-n1 mt-6"
                                    text="Diese Aktie wird mit Hilfe der Multiplikatormethode bewertet"/>
                  <h1 class="mb-2">{{ rating.share ? rating.share.name : '-' }}</h1>

                  <fin-lexicon-text auto-solve class="share-label" text="ISIN:"/>
                  <span class="share-value">{{ rating.share ? rating.share.isin : '-' }}</span>
                </v-col>
                <v-col align="right" cols="12" md="4" sm="5">
                  <v-btn :disabled="disableSaveBtn"
                         id="save-rating-button"
                         class="mr-3 font-weight-light"
                         text
                         @click="saveShareRating">
                    <v-icon class="mr-1">icon-save</v-icon>
                    Bewertung speichern
                  </v-btn>

                  <v-menu
                    color="fin-color-primary-dark"
                    left
                    offset-y
                  >
                    <template #activator="{ on }">
                      <v-btn v-on="on"
                             id="context-menu-button"
                             :style="{ borderColor: $finColors.primaryDark + ' !important'}"
                             outlined
                             small
                             text>
                        <v-icon class="fin-color-primary-dark--text">icon-contextmenu</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item :disabled="!rating.share"
                                   @click="$router.push({ name: 'companyDetail', params: { id: rating.share.id} })">
                        <v-list-item-title>
                          <v-icon :color="$finColors.primaryDark" class="pr-3">icon-arrow-link</v-icon>
                          Zur Aktie
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item :disabled="!rating.share"
                                   @click="addToWatchlist=true">
                        <v-list-item-title>
                          <v-icon :color="$finColors.primaryDark" class="pr-3">icon-eye</v-icon>
                          Auf Watchlist setzen
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item :disabled="!rating.share"
                                   @click="addShareToPortfolio">
                        <v-list-item-title>
                          <v-icon :color="$finColors.primaryDark" class="pr-3">icon-plus</v-icon>
                          Zu Portfolio hinzufügen
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>
            </v-container>

            <v-container v-if="rating.inactive">
              <v-row>
                <v-col cols="12">
                  <fin-subscription-content :feature-id="featureIds.SHARE_RATING.id"
                                            :restriction-type="featureRestrictions.UNAVAILABLE"
                                            style="max-width: 700px;"/>
                </v-col>
              </v-row>
            </v-container>

            <v-expansion-panels v-model="opened"
                                ref="shareRatingExpansionPanel"
                                accordion
                                v-if="!rating.inactive"
                                id="share-rating-expansion-panel">
              <v-container>
                <fin-classification-number-block v-for="(cn, index) in rating.conditions"
                                                 :id="index"
                                                 :key="index"
                                                 v-model="rating.conditions[index]"
                                                 :count="rating.conditions.length"
                                                 :rateParamMappings="rateParamMappings"
                                                 :sector="rating.sector"
                                                 :share="rating.share"
                                                 class="px-3 py-3 mb-4"
                                                 style="background-color: #f7f7f7 !important;"
                                                 @changeCompareGroup="isCompareGroupMode = true"
                                                 @classificationChanged="classificationChanged"
                                                 @deleteConfig="val => deleteConfig(val)"
                />
              </v-container>
            </v-expansion-panels>

            <v-container v-if="!rating.inactive" class="pb-8">
              <v-row v-if="condWeightsError || positionUndefinedError">
                <v-col>
                  <fin-error-display v-if="condWeightsError"
                                     error-msg="Die Summe der Gewichtung der einzelnen Bewertungskennzahlen muss 100% ergeben und die Gewichtungen dürfen keine Kommazahlen enthalten, bevor die Berechnung des fairen Werts möglich ist."/>

                  <fin-error-display v-if="positionUndefinedError"
                                     error-msg="Es sind nicht alle Daten für die Berechnung des Fairen Werts vorhanden. Ändere bitte die Bewertungsparameter und/oder Basis."/>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn :disabled="rating.conditions.length === rateParamMappings.length" class="mr-4" outlined
                         @click="addNewClassificationNumber">
                    <v-icon>icon-plus</v-icon>
                    Bewertungskennzahl
                  </v-btn>
                  <v-btn id="reevaluate-button" :dark="!disableReCalcBtn" :disabled="disableReCalcBtn" @click="recalc">
                    Neu bewerten
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>

            <v-container v-if="!rating.inactive">
              <v-row no-gutters>
                <v-col :style="{ backgroundColor: $finColors.paleGrey }"
                       id="course-chart-col"
                       cols="12" md="8" order="2" order-sm="1" sm="7"
                >
                  <fin-chart2 :options="chartOptions"
                              class="pt-4 pr-0 pr-sm-7"/>
                </v-col>

                <v-col :style="{ backgroundColor: $finColors.paleGrey }"
                       cols="12" md="4" order="1" order-sm="2" sm="5">
                  <v-card v-if="rating.result > 0"
                          id="fair-value-card"
                          :style="{ marginTop: $vuetify.breakpoint.sm ? '-30px' : $vuetify.breakpoint.mdAndUp ? '-75px' : '' }">
                    <v-card-subtitle class="pb-0 fair-value-subtitle">
                      FAIRER WERT
                    </v-card-subtitle>
                    <v-card-title class="pt-0 pb-6 fair-value-title">
                      <span v-if="rating.result && aboFeatureEnabled">{{ rating.result | finCurrency }}*</span>
                      <span v-else>&dash;</span>
                    </v-card-title>
                    <v-card-text>
                      <p class="pb-1 mb-0 fair-value-subtitle">AKTUELLER WERT</p>
                      <p class="pt-0 mb-0 curr-val" v-if="rating.share.latestPrice">
                        {{ rating.share.latestPrice.price | finCurrency }}</p>
                      <p v-if="rating.share.latestPrice" class="mb-4 disclaimer">
                        <span v-if="rating.share.latestPrice.exchangeName">Börse {{
                            rating.share.latestPrice.exchangeName
                          }}: </span>
                        {{ rating.share.latestPrice.timestamp | datetime }}</p>
                      <p v-else class="mb-4 disclaimer">
                        &dash;
                      </p>
                      <p class="mb-0 fair-value-subtitle">KURSPOTENZIAL</p>
                      <span v-if="rating.pricePotential && aboFeatureEnabled">
                      <v-icon v-if="rating.pricePotential > 0"
                              :color="$finColors.accentGreen"
                              class="mr-2">
                        icon-value-up
                      </v-icon>
                      <v-icon v-if="rating.pricePotential < 0"
                              :color="$finColors.accentRed"
                              class="mr-2">
                        icon-value-down
                      </v-icon>
                      <span>{{ rating.pricePotential | percent }}</span>
                    </span>
                      <span v-else>&dash;</span>

                      <p class="disclaimer">* An der Börse wird die Aktie aktuell <span
                        v-if="rating.result < rating.share.close">über</span><span v-else>unter</span> dem ermittelten
                        fairen Wert
                        gehandelt.
                      </p>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>

            <fin-add-to-watchlist :addToWatchlist="addToWatchlist"
                                  v-if="rating.share"
                                  :selected-instruments="[{ id: rating.share.id  }]"
                                  @closeAddToWatchlist="addToWatchlist=false"/>
          </div>

          <div v-else>
            <v-container class="mt-10">
              <v-row>
                <v-col>
                  <div style="margin-top: 20px; max-width: 500px; background-color: #fff; padding: 20px;">
                    <h1 class="mb-2 mb-sm-6">Es ist ein Fehler aufgetreten</h1>

                    <div>
                      <p v-if="errors.find(e => e.errorType === 'ShareRatingCalculationException')">
                        Für die Berechnung des fairen Werts liegen nicht alle benötigten Daten im System vor. <br/>
                        Versuche es zu einem späteren Zeitpunkt erneut oder wende dich mit folgendem Fehlertext
                        <br/><br/><span class="error-details">{{ errors }}</span><br/><br/>an
                        <a href="mailto:support@fincraft.de">support@fincraft.de</a>.</p>

                      <p v-else>Bei der Berechnung des fairen Werts ist ein unerwarteter Fehler aufgetreten. Bitte wende
                        dich mit folgendem Fehlertext<br/><br/><span class="error-details">{{ errors }}</span>
                        <br/><br/>an den Support.</p>

                      <v-btn outlined
                             text
                             @click="$router.push({ name: 'finToolsRateShares'})">
                        zurück zur Aktienauswahl
                      </v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </div>

        <router-view v-else :sId="rating.share.id"
                     @addToPortfolioCancel="cancelAddToPortfolio"
                     @addToPortfolioDone="successAddToPortfolio"
        />
      </template>
    </fin-data-loader>


    <div v-if="isCompareGroupMode">
      <v-container v-if="hideCustomCompareGroup" class="mt-10">
        <v-row>
          <v-col>
            <div style="margin-top: 20px; max-width: 500px; background-color: #fff; padding: 20px;">
              <h1 class="mb-2 mb-sm-6">Es ist ein Fehler aufgetreten</h1>

              <div>
                <p>
                  Für die Berechnung des fairen Werts, muss eine Aktie gewählt werden, die einer Branche zugewiesen
                  ist.<br/>Diese Info fehlt bei {{ rating.share.name }}. Wähle deshalb bitte eine andere Aktie aus.
                </p>

                <v-btn outlined
                       text
                       @click="$router.push({ name: 'finToolsRateShares'})">
                  zurück zur Aktienauswahl
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <fin-change-compare-group v-if="!hideCustomCompareGroup && rating"
                                :rating="rating"
                                @closeCompareGroupMode="isCompareGroupMode = false"/>
    </div>

  </div>
</template>

<script>
import FinLexiconText from "@/components/common/lexicon/LexiconText"
import FinClassificationNumberBlock from "@/components/pages/finTools/rate/shares/ClassificationNumberBlock"
import FinChangeCompareGroup from "@/components/pages/finTools/rate/shares/ChangeCompareGroup"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {getPositionGroupMembers, getShareRating} from "@/views/protected/finTools/rate/shares/graphql"
import FinDataLoader from "@/components/common/DataLoader"
import FinAddToWatchlist from "@/components/common/Watchlists/AddToWatchlist"
import {finShareRatingAPI, finTimeSeriesAPI} from "@/js/finAPI"
import FinChart2 from "@/components/common/Chart2/FinChart2"
import {DEFAULT_CHART_OPTIONS} from "@/components/common/Chart2/HighChartInit"
import FinErrorDisplay from "@/components/common/ErrorDisplay"
import {EventBus} from "@/plugins/fincraft"
import PriceModel from "@/mixins/PriceModel"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import FinSubscriptionContent from "@/components/common/subscription/content"

export default {
  name: "FinRateSharesRating",
  components: {
    FinSubscriptionContent,
    FinErrorDisplay,
    FinChart2,
    FinAddToWatchlist,
    FinDataLoader,
    FinChangeCompareGroup,
    FinLexiconText,
    FinClassificationNumberBlock
  },
  props: {
    // set via router-link => share rating id - tool-id
    srId: {
      type: String
    },
    // set via router-link => share id = new share rating
    sid: {
      type: String
    },
  },
  data() {
    return {
      rateParamMappings: [],
      rating: null,
      initialLoad: true,
      isCompareGroupMode: false,
      addToWatchlist: false,
      openedProxy: 0,
      condWeightsError: false,
      positionUndefinedError: false,
      sectorTS: [],
      disableSaveBtn: true,
      disableReCalcBtn: true,
      isAddToPortfolioMode: false,
      errors: null,
      hideCustomCompareGroup: process.env.VUE_APP_TOOLS_BDA_HIDE_CUSTOMGROUP === 'true',
      featureIds: PRICEMODEL_FEATURE_IDS,
      featureRestrictions: PRICEMODEL_FEATURE_RESTRICTIONS,
    }
  },
  mixins: [PriceModel],
  computed: {
    aboFeatureEnabled() {
      return this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.SHARE_RATING.id)
    },
    chartOptions() {
      let series = []

      if (this.rating.share.mainListing.timeSeries) {
        series.push({
          name: this.rating.share.name,
          color: this.$finColors.primaryDark,
          data: this.rating.share.mainListing.timeSeries.map(ts => [new Date(ts.timestamp).getTime(), ts.close]),
          tooltip: {
            value: 'y',
            valueSuffix: ' €'
          }
        })
        if (this.sectorTS) {
          series.push({
            name: 'Branche',
            color: "#AFB6BA",
            data: this.sectorTS.map(ts => [new Date(ts.timestamp).getTime(), ts.value]),
            tooltip: {
              valueFormat: (value, change) => change,
            }
          })
        }
      }

      return {
        preset: "percentCompare",
        title: {
          text: "<h5>Aktuelle Kursentwicklung (%)</h5>",
          useHTML: true,
          align: 'left',
        },
        legend: {
          enabled: true
        },
        rangeSelector: {
          selected: DEFAULT_CHART_OPTIONS.rangeSelector.buttons.findIndex(r => r.finTimeSeriesSuffix === "1Y")
        },
        series
      }
    },
    opened: {
      get() {
        return this.openedProxy
      },
      set(newVal) {
        this.openedProxy = newVal
      }
    },
  },
  methods: {
    addShareToPortfolio() {
      this.$router.push({
        name: this.$route.name === 'finToolsRateSharesRatingExec' ? 'addShareRateExecToPortfolio' : 'addShareRateToPortfolio',
        params: {
          //instruments: this.selectedItems,
          title: 'Wertpapier in Portfolio hinzufügen',
          to: {name: 'finToolsRateShares', params: {srId: this.srId, sid: this.rating.share.id}}
        }
      })
    },
    async loadData() {
      // create mode => load share by url path param
      if (this.sid && this.sid.length > 0) {
        try {
          this.rating = await finShareRatingAPI.getDefaultShareRating(this.sid, this.aboFeatureEnabled)
        } catch (error) {
          this.errors = error.errors.map(e => e.message = JSON.parse(e.message))
          return
        }

        // no sector is set which is needed for calc of fair value -> switch mode to compare group selection
        if (!this.rating.share.company.sectorLevel1) {
          this.isCompareGroupMode = true
        }
        // enable buttons if sector is available
        else {
          this.disableSaveBtn = false
          this.disableReCalcBtn = false
        }
      }

      // edit mode => load share rating by url path param
      if (this.srId && this.srId.length > 0) {
        let result = await API.graphql(graphqlOperation(getShareRating, {
          id: this.srId
        }))

        this.rating = result.data.getShareRating

        // disable re-calc + save buttons if the share rating has already been rated today
        const now = new Date().toDateString()
        const lastRated = new Date(this.rating.ratedAt).toDateString()
        const disableRecalcSaveBtns = lastRated === now

        this.disableReCalcBtn = disableRecalcSaveBtns
        this.disableSaveBtn = disableRecalcSaveBtns
      }

      if (!this.rating.inactive && this.rating.sector.id) {
        this.sectorTS = await finTimeSeriesAPI.getSectorTimeSeries(this.rating.sector.id, this.rating.share.id)
      }

      if (!this.rating.inactive) {
        let posGroupMembers = await API.graphql(graphqlOperation(getPositionGroupMembers))
        posGroupMembers = posGroupMembers.data.getPositionGroup.entries.items

        posGroupMembers.forEach(pgm => {
          let spg = pgm.subPosGroup
          spg.entries.items.forEach(e => {
              let item = e.dataItem
              let t = {
                text: spg.name,
                key: item.id,
                mappedKey: item.dividerItemId,
                label: item.dividerItemName
              }
              this.rateParamMappings.push(t)
            }
          )
        })
      }

      if (this.rating.inactive) {
        this.disableSaveBtn = true
      }
    },
    addNewClassificationNumber() {
      let count = this.rating.conditions.length + 1
      let diff = 100 % count
      let ratio = (100 - diff) / count

      const usedRatios = []
      this.rating.conditions.forEach(c => {
        c.weight = ratio
        usedRatios.push(c.financialDataItemId)
      })

      // used to select the next possible rateParam
      const filteredRatios = this.rateParamMappings.filter(rpm => !usedRatios.includes(rpm.key))

      let newClassification = {
        initial: false,
        financialDataItemId: filteredRatios.length > 0 ? filteredRatios[0].key : 'peRatio',
        basis: 'CURRENT_YEAR',
        weight: ratio + diff,
        customSharePositionValue: null,
      }
      this.rating.conditions.push(newClassification)

      // enable buttons
      this.disableSaveBtn = false
      this.disableReCalcBtn = false

      // open this new classification block
      this.openedProxy++
    },
    deleteConfig(val) {
      let conditions = this.rating.conditions
      conditions.splice(val, 1)

      let count = conditions.length
      let diff = 100 % count
      let ratio = (100 - diff) / count

      conditions.forEach(c => c.weight = ratio)

      conditions[conditions.length - 1].weight = ratio + diff

      this.rating.conditions = conditions

      // enable buttons
      this.disableSaveBtn = false
      this.disableReCalcBtn = false

      // open previous classification block
      this.openedProxy--
    },
    saveShareRating() {
      if (this.rating.id === "null") {
        this.$confirm({
          title: 'Willst du diese Bewertung speichern?',
          titleWithLexikonLink: true,
          text: 'Es werden alle ausgewählten Kennzahlen, die Basis sowie die konfigurierte Gewichtung gespeichert.',
          textWithLexikonLink: true,
          input: true,
          inputMultiline: true,
          inputLabel: 'Wie soll deine neue Bewertung heißen?',
          inputPlaceholder: 'Meine ' + this.rating.share.name + ' Bewertung',
          confirmBtn: 'Speichern'
        }).then((input) => {
          this.rating.name = input.inputText
          this.doSave()
        })
      } else {
        this.doSave()
      }
    },
    doSave() {
      let ratingInput = {
        name: this.rating.name,
        shareRatingShareId: this.rating.share.id,
        conditions: this.rating.conditions.map(c => ({
          initial: c.initial,
          financialDataItemId: c.financialDataItemId,
          customSharePositionValue: c.customSharePositionValue,
          basis: c.basis,
          weight: c.weight
        })),
        shareRatingSectorId: this.rating.sector.id,
      }

      if (this.rating.id !== "null") {
        ratingInput.id = this.rating.id
      }

      finShareRatingAPI.calculateAndSaveShareRating(ratingInput).then(result => {
        // set it so the watch doesn't get triggered
        this.initialLoad = true
        this.disableSaveBtn = true
        this.disableReCalcBtn = true

        this.rating = result
        this.$toasted.global.finToast({
          message: 'Deine Bewertung wurde erfolgreich gespeichert.',
          type: 'success'
        })
      }).catch(error => {
        console.error("ERROR saving share rating", ratingInput, error)
        this.$toasted.global.finToast({
          message: 'Beim Speichern deiner Bewertung trat ein Fehler auf. Lade die Seite erneut und wiederhole die Aktion gegebenenfalls.',
          type: 'error'
        })
      })
    },
    recalc() {
      this.positionUndefinedError = false
      if (!this.aboFeatureEnabled) {
        EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.SHARE_RATING.id, PRICEMODEL_FEATURE_RESTRICTIONS.UNAVAILABLE)
      } else {
        let ratingInput = {
          id: this.rating.id,
          name: this.rating.name,
          shareRatingShareId: this.rating.share.id,
          conditions: this.rating.conditions.map(c => ({
            initial: c.initial,
            financialDataItemId: c.financialDataItemId,
            customSharePositionValue: c.customSharePositionValue,
            basis: c.basis,
            weight: c.weight
          })),
          shareRatingSectorId: this.rating.sector.id,
        }

        finShareRatingAPI.calculateShareRating(ratingInput).then(result => {
          // set it so the watch doesn't get triggered
          this.initialLoad = true
          this.disableReCalcBtn = true

          this.rating = result
          this.$toasted.global.finToast({
            message: 'Deine Bewertung wurde erfolgreich aktualisiert.',
            type: 'success'
          })
        }).catch(error => {
          console.error("ERROR recalculating share rating", ratingInput, error)
          if (error.errors.find(e => e.errorType === 'PositionUndefinedException')) {
            this.positionUndefinedError = true
          } else {
            this.$toasted.global.finToast({
              message: 'Beim Aktualisieren deiner Bewertung trat ein Fehler auf. Lade die Seite erneut und wiederhole die Aktion gegebenenfalls.',
              type: 'error'
            })

            this.errors = error.errors
            this.rating = null
          }
        })
      }
    },
    cancelAddToPortfolio() {
      console.log("cancelAddToPortfolio")
      this.isAddToPortfolioMode = false
      this.isCompareGroupMode = false
    },
    successAddToPortfolio() {
      this.isAddToPortfolioMode = false
      this.isCompareGroupMode = false
    },
    classificationChanged() {
      this.disableReCalcBtn = false
      this.disableSaveBtn = false
    }
  },
  watch: {
    rating: {
      deep: true,
      handler() {
        if (this.initialLoad) {
          this.initialLoad = false
          return
        }

        // no need to calc weights if there is just 1 condition (weight = implicitly 100%)
        if (this.rating.conditions && this.rating.conditions.length > 1) {
          let skipSumCheck = false

          this.rating.conditions.forEach(rc => {
            const weightString = '' + rc.weight
            if (weightString.includes('.') || weightString.includes(',')) {
              this.condWeightsError = true
              this.disableSaveBtn = true
              this.disableReCalcBtn = true
              skipSumCheck = true
            }
          })

          if (!skipSumCheck) {
            // check all condition weights to be equal 100%, otherwise block save + recalc
            let total = this.rating.conditions.reduce((prevVal, currVal) => prevVal + Number(currVal.weight), 0)

            if (total !== 100) {
              this.condWeightsError = true
              this.disableSaveBtn = true
              this.disableReCalcBtn = true
            } else {
              this.condWeightsError = false
              this.disableSaveBtn = false
              this.disableReCalcBtn = false
            }
          }
        }
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.v-expansion-panel::before {
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
}

.method-info {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.share-label {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.39;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
  /* additional */
  display: inline-block;
  margin-right: 4px;
}

.share-value {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.fair-value-subtitle {
  font-family: $body-font-family;
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.17;
  letter-spacing: 1px;
  color: $fin-color-primary-dark !important;
}

.fair-value-title {
  font-family: $body-font-family;
  font-size: 42px !important;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2 !important;
  letter-spacing: normal;
  color: #01083c;
}

.curr-val {
  font-family: $body-font-family;
  font-size: 18px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.disclaimer {
  font-family: $body-font-family;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: $fin-color-cool-grey;
}

.error-details {
  font-family: $body-font-family;
  font-size: 12px;
  color: $fin-color-pale-red;
}

::v-deep .highcharts-title {
  width: 100% !important;
}
</style>
